import { ThreatClass, type ThreatClassId } from '../config/threats';
import { TargetKind, WarheadKind, type TargetKindId, type WarheadKindId } from '../config/targets';

/**
 * The mission data format (SPEC §6.1) and its runtime validator.
 *
 * Missions are pure data so the campaign can be authored in bulk and, later, by players in an
 * editor (SPEC §3.10.1). That makes validation load-bearing rather than defensive: a JSON file has
 * no compiler behind it, and SPEC's M8 note is explicit — *"you will author twelve of these files
 * and silent failures will cost hours"*.
 *
 * So the validator reports **every** violation with a JSON path, not just the first. A loader that
 * throws on the first bad field turns authoring twelve missions into twelve compile-run cycles per
 * mission; one that lists all fourteen problems at once turns it into one.
 *
 * Angles in degrees here — this is a config boundary (CLAUDE.md), converted at the edge.
 */

export type WaypointKind = 'nav' | 'ip' | 'target' | 'egress';
export type ObjectiveKind = 'destroy' | 'reach' | 'survive' | 'avoid' | 'photograph';
export type ObjectiveState = 'pending' | 'complete' | 'failed';

export interface MissionBrief {
  readonly summary: string;
  readonly intel: readonly string[];
  readonly rulesOfEngagement?: readonly string[];
}

export interface MissionEnvironment {
  readonly timeOfDay: number;
  readonly windDirDeg: number;
  readonly windSpeedMs: number;
  readonly turbulence: number;
  readonly visibilityKm: number;
  readonly cloudCover: number;
  readonly seaState: number;
}

export interface MissionLaunch {
  readonly position: readonly [number, number, number];
  readonly headingDeg: number;
  readonly altitudeM: number;
  readonly airspeedMs: number;
}

export interface MissionLoadout {
  readonly allowed: readonly string[];
  readonly default: readonly string[];
  readonly chaff: number;
  readonly flares: number;
  readonly warhead: WarheadKindId;
}

export interface MissionWaypoint {
  readonly id: string;
  readonly position: readonly [number, number, number];
  readonly label: string;
  readonly type: WaypointKind;
  readonly revealedFrom?: string;
}

export interface MissionThreat {
  readonly id: string;
  readonly type: ThreatClassId;
  readonly position: readonly [number, number, number];
  readonly headingDeg?: number;
  readonly active: boolean;
  readonly overrides?: Readonly<Record<string, number>>;
  readonly patrol?: readonly (readonly [number, number, number])[];
}

export interface MissionTarget {
  readonly id: string;
  readonly type: TargetKindId;
  readonly position: readonly [number, number, number];
  readonly label: string;
  readonly headingDeg?: number;
  /** Optional per-mission overrides of the catalogue entry (SPEC §6.1). */
  readonly hardness?: number;
  readonly requiredDiveAngleDeg?: number;
  readonly requiredImpactSpeedMs?: number;
}

export interface MissionObjective {
  readonly id: string;
  readonly kind: ObjectiveKind;
  readonly targetRef?: string;
  readonly primary: boolean;
  readonly description: string;
  readonly weight: number;
  /** `survive`/`avoid` need a bound; `reach` uses the waypoint radius. */
  readonly seconds?: number;
  readonly radiusM?: number;
}

export type MissionTrigger =
  | { readonly t: 'zone'; readonly center: readonly [number, number, number]; readonly radius: number }
  | { readonly t: 'time'; readonly seconds: number }
  | { readonly t: 'objective'; readonly ref: string; readonly state: 'complete' | 'failed' }
  | { readonly t: 'altitude'; readonly below?: number; readonly above?: number }
  | { readonly t: 'threatState'; readonly ref: string; readonly state: string }
  | { readonly t: 'damage'; readonly above: number };

export type MissionAction =
  | { readonly a: 'activateThreat'; readonly ref: string }
  | { readonly a: 'deactivateThreat'; readonly ref: string }
  | { readonly a: 'spawn'; readonly type: string; readonly position: readonly [number, number, number] }
  | { readonly a: 'radio'; readonly speaker: string; readonly text: string; readonly durationS: number }
  | { readonly a: 'revealWaypoint'; readonly ref: string }
  | { readonly a: 'setObjective'; readonly ref: string; readonly state: ObjectiveState }
  | { readonly a: 'setWeather'; readonly patch: Partial<MissionEnvironment> };

export interface MissionEvent {
  readonly id: string;
  readonly trigger: MissionTrigger;
  readonly once: boolean;
  readonly actions: readonly MissionAction[];
}

export interface MissionScoring {
  readonly parTimeS: number;
  readonly weights: Readonly<Record<string, number>>;
  readonly expertRequires: readonly string[];
}

export interface Mission {
  readonly id: string;
  readonly title: string;
  readonly order: number;
  readonly theatre: 'kerani';
  readonly brief: MissionBrief;
  readonly environment: MissionEnvironment;
  readonly launch: MissionLaunch;
  readonly airframe: string;
  readonly loadout: MissionLoadout;
  readonly route: readonly MissionWaypoint[];
  readonly threats: readonly MissionThreat[];
  readonly targets: readonly MissionTarget[];
  readonly objectives: readonly MissionObjective[];
  readonly events: readonly MissionEvent[];
  readonly scoring: MissionScoring;
}

/** One problem with a mission file, located precisely enough to fix without searching. */
export interface ValidationIssue {
  /** JSON path, e.g. `threats[2].type`. */
  readonly path: string;
  readonly message: string;
}

export class MissionValidationError extends Error {
  readonly issues: readonly ValidationIssue[];

  constructor(missionId: string, issues: readonly ValidationIssue[]) {
    const body = issues.map((issue) => `  ${issue.path}: ${issue.message}`).join('\n');
    super(`Mission "${missionId}" failed validation with ${issues.length} problem(s):\n${body}`);
    this.name = 'MissionValidationError';
    this.issues = issues;
  }
}

const WAYPOINT_KINDS: readonly WaypointKind[] = ['nav', 'ip', 'target', 'egress'];
const OBJECTIVE_KINDS: readonly ObjectiveKind[] = [
  'destroy',
  'reach',
  'survive',
  'avoid',
  'photograph',
];
const OBJECTIVE_STATES: readonly ObjectiveState[] = ['pending', 'complete', 'failed'];
const THREAT_TYPES = Object.values(ThreatClass) as readonly string[];
const TARGET_TYPES = Object.values(TargetKind) as readonly string[];
const WARHEADS = Object.values(WarheadKind) as readonly string[];
/**
 * The numeric fields of `ThreatParams` a mission may retune per site (SPEC §6.1 `overrides`).
 *
 * Listed explicitly rather than derived, because the derivation would need a live params object
 * and this has to run against parsed JSON before any site exists. Non-numeric identity fields
 * (`id`, `rwrLabel`, `emitsRadar`) are deliberately absent: a mission may tune a threat, not
 * change what kind of threat it is.
 */
const OVERRIDABLE_THREAT_FIELDS: readonly string[] = [
  'searchRangeM',
  'trackRangeM',
  'minEngageM',
  'maxEngageM',
  'minAltM',
  'maxAltM',
  'acquireTimeS',
  'reactionTimeS',
  'trackMemoryS',
  'notchVelocityMs',
  'salvoSize',
  'salvoIntervalS',
  'reloadS',
  'magazine',
  'radarMastHeightM',
  'crewQuality',
];

const TRIGGER_KINDS = ['zone', 'time', 'objective', 'altitude', 'threatState', 'damage'];
const ACTION_KINDS = [
  'activateThreat',
  'deactivateThreat',
  'spawn',
  'radio',
  'revealWaypoint',
  'setObjective',
  'setWeather',
];

/**
 * Collects issues rather than throwing, so one pass reports everything wrong with a file.
 *
 * Every accessor is defensive about the *shape* it was handed, because the input is `unknown`
 * parsed JSON — a mission file may be missing whole objects, not just fields.
 */
class Validator {
  readonly issues: ValidationIssue[] = [];

  fail(path: string, message: string): void {
    this.issues.push({ path, message });
  }

  /** An object, or null with an issue recorded. */
  object(value: unknown, path: string): Record<string, unknown> | null {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      this.fail(path, `expected an object, got ${describe(value)}`);
      return null;
    }
    return value as Record<string, unknown>;
  }

  array(value: unknown, path: string): unknown[] | null {
    if (!Array.isArray(value)) {
      this.fail(path, `expected an array, got ${describe(value)}`);
      return null;
    }
    return value;
  }

  string(value: unknown, path: string): string {
    if (typeof value !== 'string' || value.length === 0) {
      this.fail(path, `expected a non-empty string, got ${describe(value)}`);
      return '';
    }
    return value;
  }

  number(value: unknown, path: string, min?: number, max?: number): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      this.fail(path, `expected a finite number, got ${describe(value)}`);
      return 0;
    }
    if (min !== undefined && value < min) {
      this.fail(path, `must be at least ${min}, got ${value}`);
    }
    if (max !== undefined && value > max) {
      this.fail(path, `must be at most ${max}, got ${value}`);
    }
    return value;
  }

  boolean(value: unknown, path: string): boolean {
    if (typeof value !== 'boolean') {
      this.fail(path, `expected a boolean, got ${describe(value)}`);
      return false;
    }
    return value;
  }

  oneOf<T extends string>(value: unknown, path: string, allowed: readonly string[]): T {
    if (typeof value !== 'string' || !allowed.includes(value)) {
      this.fail(path, `expected one of ${allowed.join(' | ')}, got ${describe(value)}`);
      return allowed[0] as T;
    }
    return value as T;
  }

  vec3(value: unknown, path: string): [number, number, number] {
    if (!Array.isArray(value) || value.length !== 3) {
      this.fail(path, `expected [x, y, z], got ${describe(value)}`);
      return [0, 0, 0];
    }
    return [
      this.number(value[0], `${path}[0]`),
      this.number(value[1], `${path}[1]`),
      this.number(value[2], `${path}[2]`),
    ];
  }

  stringArray(value: unknown, path: string): string[] {
    const raw = this.array(value, path);
    if (raw === null) return [];
    return raw.map((entry, i) => this.string(entry, `${path}[${i}]`));
  }
}

function describe(value: unknown): string {
  if (value === undefined) return 'nothing';
  if (value === null) return 'null';
  if (Array.isArray(value)) return `an array of ${value.length}`;
  return `${typeof value} (${JSON.stringify(value)?.slice(0, 40) ?? '?'})`;
}

/**
 * Validate parsed JSON against the §6.1 schema.
 *
 * Returns the mission on success; throws `MissionValidationError` listing every problem otherwise.
 * Cross-references are checked as well as shapes — an objective naming a target that does not
 * exist is exactly the kind of mistake that survives a shape-only check and then fails silently
 * at 10 Hz for the rest of the mission.
 */
export function validateMission(raw: unknown): Mission {
  const v = new Validator();
  const root = v.object(raw, '$');
  if (root === null) throw new MissionValidationError('<unparseable>', v.issues);

  const id = v.string(root.id, 'id');
  const title = v.string(root.title, 'title');
  const order = v.number(root.order, 'order', 0);
  const theatre = v.oneOf<'kerani'>(root.theatre, 'theatre', ['kerani']);
  const airframe = v.string(root.airframe, 'airframe');

  const brief = validateBrief(v, root.brief);
  const environment = validateEnvironment(v, root.environment, 'environment');
  const launch = validateLaunch(v, root.launch);
  const loadout = validateLoadout(v, root.loadout);
  const route = validateRoute(v, root.route);
  const threats = validateThreats(v, root.threats);
  const targets = validateTargets(v, root.targets);
  const objectives = validateObjectives(v, root.objectives);
  const events = validateEvents(v, root.events);
  const scoring = validateScoring(v, root.scoring);

  crossReference(v, { route, threats, targets, objectives, events });

  if (v.issues.length > 0) throw new MissionValidationError(id || '<unnamed>', v.issues);

  return {
    id,
    title,
    order,
    theatre,
    brief,
    environment,
    launch,
    airframe,
    loadout,
    route,
    threats,
    targets,
    objectives,
    events,
    scoring,
  };
}

function validateBrief(v: Validator, raw: unknown): MissionBrief {
  const o = v.object(raw, 'brief');
  if (o === null) return { summary: '', intel: [] };
  const roe = o.rulesOfEngagement === undefined
    ? undefined
    : v.stringArray(o.rulesOfEngagement, 'brief.rulesOfEngagement');
  return {
    summary: v.string(o.summary, 'brief.summary'),
    intel: v.stringArray(o.intel, 'brief.intel'),
    ...(roe === undefined ? {} : { rulesOfEngagement: roe }),
  };
}

function validateEnvironment(v: Validator, raw: unknown, path: string): MissionEnvironment {
  const o = v.object(raw, path);
  if (o === null) {
    return {
      timeOfDay: 12,
      windDirDeg: 0,
      windSpeedMs: 0,
      turbulence: 0,
      visibilityKm: 20,
      cloudCover: 0,
      seaState: 0,
    };
  }
  return {
    timeOfDay: v.number(o.timeOfDay, `${path}.timeOfDay`, 0, 24),
    windDirDeg: v.number(o.windDirDeg, `${path}.windDirDeg`, 0, 360),
    windSpeedMs: v.number(o.windSpeedMs, `${path}.windSpeedMs`, 0),
    turbulence: v.number(o.turbulence, `${path}.turbulence`, 0, 1),
    visibilityKm: v.number(o.visibilityKm, `${path}.visibilityKm`, 0),
    cloudCover: v.number(o.cloudCover, `${path}.cloudCover`, 0, 1),
    seaState: v.number(o.seaState, `${path}.seaState`, 0, 5),
  };
}

function validateLaunch(v: Validator, raw: unknown): MissionLaunch {
  const o = v.object(raw, 'launch');
  if (o === null) {
    return { position: [0, 0, 0], headingDeg: 0, altitudeM: 0, airspeedMs: 0 };
  }
  return {
    position: v.vec3(o.position, 'launch.position'),
    headingDeg: v.number(o.headingDeg, 'launch.headingDeg', 0, 360),
    altitudeM: v.number(o.altitudeM, 'launch.altitudeM', 0),
    airspeedMs: v.number(o.airspeedMs, 'launch.airspeedMs', 1),
  };
}

function validateLoadout(v: Validator, raw: unknown): MissionLoadout {
  const o = v.object(raw, 'loadout');
  if (o === null) {
    return { allowed: [], default: [], chaff: 0, flares: 0, warhead: WarheadKind.Fragmentation };
  }
  const allowed = v.stringArray(o.allowed, 'loadout.allowed');
  const chosen = v.stringArray(o.default, 'loadout.default');
  for (let i = 0; i < chosen.length; i += 1) {
    if (chosen[i] !== '' && !allowed.includes(chosen[i])) {
      v.fail(`loadout.default[${i}]`, `"${chosen[i]}" is not in loadout.allowed`);
    }
  }
  return {
    allowed,
    default: chosen,
    chaff: v.number(o.chaff, 'loadout.chaff', 0),
    flares: v.number(o.flares, 'loadout.flares', 0),
    warhead: v.oneOf<WarheadKindId>(o.warhead, 'loadout.warhead', WARHEADS),
  };
}

function validateRoute(v: Validator, raw: unknown): MissionWaypoint[] {
  const list = v.array(raw, 'route');
  if (list === null) return [];
  return list.map((entry, i) => {
    const path = `route[${i}]`;
    const o = v.object(entry, path);
    if (o === null) return { id: '', position: [0, 0, 0] as const, label: '', type: 'nav' as const };
    const revealedFrom =
      o.revealedFrom === undefined ? undefined : v.string(o.revealedFrom, `${path}.revealedFrom`);
    return {
      id: v.string(o.id, `${path}.id`),
      position: v.vec3(o.position, `${path}.position`),
      label: v.string(o.label, `${path}.label`),
      type: v.oneOf<WaypointKind>(o.type, `${path}.type`, WAYPOINT_KINDS),
      ...(revealedFrom === undefined ? {} : { revealedFrom }),
    };
  });
}

function validateThreats(v: Validator, raw: unknown): MissionThreat[] {
  const list = v.array(raw, 'threats');
  if (list === null) return [];
  return list.map((entry, i) => {
    const path = `threats[${i}]`;
    const o = v.object(entry, path);
    if (o === null) {
      return {
        id: '',
        type: ThreatClass.ShoradTracked,
        position: [0, 0, 0] as const,
        active: false,
      };
    }
    let overrides: Record<string, number> | undefined;
    if (o.overrides !== undefined) {
      const patch = v.object(o.overrides, `${path}.overrides`);
      if (patch !== null) {
        overrides = {};
        for (const key of Object.keys(patch)) {
          // Override keys are checked against the real parameter names. A typo here would
          // otherwise be written into an object nobody reads: the mission loads, the site uses
          // its defaults, and the tuning the author intended silently never happens.
          if (!OVERRIDABLE_THREAT_FIELDS.includes(key)) {
            v.fail(
              `${path}.overrides.${key}`,
              `not a threat parameter (expected one of ${OVERRIDABLE_THREAT_FIELDS.join(', ')})`,
            );
            continue;
          }
          overrides[key] = v.number(patch[key], `${path}.overrides.${key}`);
        }
      }
    }
    let patrol: [number, number, number][] | undefined;
    if (o.patrol !== undefined) {
      const legs = v.array(o.patrol, `${path}.patrol`);
      if (legs !== null) patrol = legs.map((leg, j) => v.vec3(leg, `${path}.patrol[${j}]`));
    }
    const headingDeg =
      o.headingDeg === undefined ? undefined : v.number(o.headingDeg, `${path}.headingDeg`, 0, 360);
    return {
      id: v.string(o.id, `${path}.id`),
      type: v.oneOf<ThreatClassId>(o.type, `${path}.type`, THREAT_TYPES),
      position: v.vec3(o.position, `${path}.position`),
      active: v.boolean(o.active, `${path}.active`),
      ...(headingDeg === undefined ? {} : { headingDeg }),
      ...(overrides === undefined ? {} : { overrides }),
      ...(patrol === undefined ? {} : { patrol }),
    };
  });
}

function validateTargets(v: Validator, raw: unknown): MissionTarget[] {
  const list = v.array(raw, 'targets');
  if (list === null) return [];
  return list.map((entry, i) => {
    const path = `targets[${i}]`;
    const o = v.object(entry, path);
    if (o === null) {
      return { id: '', type: TargetKind.RadarAntenna, position: [0, 0, 0] as const, label: '' };
    }
    const optionalNumber = (key: string, min?: number, max?: number): number | undefined =>
      o[key] === undefined ? undefined : v.number(o[key], `${path}.${key}`, min, max);
    const hardness = optionalNumber('hardness', 0, 1);
    const dive = optionalNumber('requiredDiveAngleDeg', 0, 90);
    const speed = optionalNumber('requiredImpactSpeedMs', 0);
    const headingDeg = optionalNumber('headingDeg', 0, 360);
    return {
      id: v.string(o.id, `${path}.id`),
      type: v.oneOf<TargetKindId>(o.type, `${path}.type`, TARGET_TYPES),
      position: v.vec3(o.position, `${path}.position`),
      label: v.string(o.label, `${path}.label`),
      ...(headingDeg === undefined ? {} : { headingDeg }),
      ...(hardness === undefined ? {} : { hardness }),
      ...(dive === undefined ? {} : { requiredDiveAngleDeg: dive }),
      ...(speed === undefined ? {} : { requiredImpactSpeedMs: speed }),
    };
  });
}

function validateObjectives(v: Validator, raw: unknown): MissionObjective[] {
  const list = v.array(raw, 'objectives');
  if (list === null) return [];
  if (list.length === 0) v.fail('objectives', 'a mission needs at least one objective');
  const objectives = list.map((entry, i) => {
    const path = `objectives[${i}]`;
    const o = v.object(entry, path);
    if (o === null) {
      return {
        id: '',
        kind: 'destroy' as const,
        primary: true,
        description: '',
        weight: 0,
      };
    }
    const kind = v.oneOf<ObjectiveKind>(o.kind, `${path}.kind`, OBJECTIVE_KINDS);
    const targetRef = o.targetRef === undefined ? undefined : v.string(o.targetRef, `${path}.targetRef`);
    const seconds = o.seconds === undefined ? undefined : v.number(o.seconds, `${path}.seconds`, 0);
    const radiusM = o.radiusM === undefined ? undefined : v.number(o.radiusM, `${path}.radiusM`, 1);

    // Kind-specific requirements. These are the mistakes that otherwise fail silently: an
    // objective with nothing to check against simply never completes, and the mission is
    // unwinnable in a way no shape check would catch.
    if ((kind === 'destroy' || kind === 'photograph') && targetRef === undefined) {
      v.fail(`${path}.targetRef`, `a "${kind}" objective must name a target`);
    }
    if (kind === 'reach' && targetRef === undefined) {
      v.fail(`${path}.targetRef`, 'a "reach" objective must name a waypoint');
    }
    if (kind === 'survive' && seconds === undefined) {
      v.fail(`${path}.seconds`, 'a "survive" objective must set seconds');
    }
    if (kind === 'avoid' && targetRef === undefined) {
      v.fail(`${path}.targetRef`, 'an "avoid" objective must name what to avoid');
    }

    return {
      id: v.string(o.id, `${path}.id`),
      kind,
      primary: v.boolean(o.primary, `${path}.primary`),
      description: v.string(o.description, `${path}.description`),
      weight: v.number(o.weight, `${path}.weight`, 0),
      ...(targetRef === undefined ? {} : { targetRef }),
      ...(seconds === undefined ? {} : { seconds }),
      ...(radiusM === undefined ? {} : { radiusM }),
    };
  });

  if (objectives.length > 0 && !objectives.some((objective) => objective.primary)) {
    v.fail('objectives', 'at least one objective must be primary, or the mission cannot be won');
  }
  return objectives;
}

function validateEvents(v: Validator, raw: unknown): MissionEvent[] {
  const list = v.array(raw, 'events');
  if (list === null) return [];
  return list.map((entry, i) => {
    const path = `events[${i}]`;
    const o = v.object(entry, path);
    if (o === null) {
      return { id: '', trigger: { t: 'time' as const, seconds: 0 }, once: true, actions: [] };
    }
    return {
      id: v.string(o.id, `${path}.id`),
      trigger: validateTrigger(v, o.trigger, `${path}.trigger`),
      once: v.boolean(o.once, `${path}.once`),
      actions: validateActions(v, o.actions, `${path}.actions`),
    };
  });
}

function validateTrigger(v: Validator, raw: unknown, path: string): MissionTrigger {
  const o = v.object(raw, path);
  if (o === null) return { t: 'time', seconds: 0 };
  const t = v.oneOf<MissionTrigger['t']>(o.t, `${path}.t`, TRIGGER_KINDS);
  switch (t) {
    case 'zone':
      return {
        t,
        center: v.vec3(o.center, `${path}.center`),
        radius: v.number(o.radius, `${path}.radius`, 1),
      };
    case 'time':
      return { t, seconds: v.number(o.seconds, `${path}.seconds`, 0) };
    case 'objective':
      return {
        t,
        ref: v.string(o.ref, `${path}.ref`),
        state: v.oneOf<'complete' | 'failed'>(o.state, `${path}.state`, ['complete', 'failed']),
      };
    case 'altitude': {
      const below = o.below === undefined ? undefined : v.number(o.below, `${path}.below`);
      const above = o.above === undefined ? undefined : v.number(o.above, `${path}.above`);
      if (below === undefined && above === undefined) {
        v.fail(path, 'an altitude trigger needs "below", "above", or both');
      }
      return { t, ...(below === undefined ? {} : { below }), ...(above === undefined ? {} : { above }) };
    }
    case 'threatState':
      return { t, ref: v.string(o.ref, `${path}.ref`), state: v.string(o.state, `${path}.state`) };
    case 'damage':
      return { t, above: v.number(o.above, `${path}.above`, 0, 1) };
    default:
      return { t: 'time', seconds: 0 };
  }
}

function validateActions(v: Validator, raw: unknown, path: string): MissionAction[] {
  const list = v.array(raw, path);
  if (list === null) return [];
  if (list.length === 0) v.fail(path, 'an event with no actions does nothing');
  return list.map((entry, i) => {
    const at = `${path}[${i}]`;
    const o = v.object(entry, at);
    if (o === null) return { a: 'radio' as const, speaker: '', text: '', durationS: 0 };
    const a = v.oneOf<MissionAction['a']>(o.a, `${at}.a`, ACTION_KINDS);
    switch (a) {
      case 'activateThreat':
      case 'deactivateThreat':
        return { a, ref: v.string(o.ref, `${at}.ref`) };
      case 'spawn':
        return {
          a,
          type: v.oneOf<string>(o.type, `${at}.type`, THREAT_TYPES),
          position: v.vec3(o.position, `${at}.position`),
        };
      case 'radio':
        return {
          a,
          speaker: v.string(o.speaker, `${at}.speaker`),
          text: v.string(o.text, `${at}.text`),
          durationS: v.number(o.durationS, `${at}.durationS`, 0.5),
        };
      case 'revealWaypoint':
        return { a, ref: v.string(o.ref, `${at}.ref`) };
      case 'setObjective':
        return {
          a,
          ref: v.string(o.ref, `${at}.ref`),
          state: v.oneOf<ObjectiveState>(o.state, `${at}.state`, OBJECTIVE_STATES),
        };
      case 'setWeather':
        return { a, patch: validateWeatherPatch(v, o.patch, `${at}.patch`) };
      default:
        return { a: 'radio' as const, speaker: '', text: '', durationS: 1 };
    }
  });
}

function validateWeatherPatch(
  v: Validator,
  raw: unknown,
  path: string,
): Partial<MissionEnvironment> {
  const o = v.object(raw, path);
  if (o === null) return {};
  const patch: Record<string, number> = {};
  const bounds: Record<string, [number, number | undefined]> = {
    timeOfDay: [0, 24],
    windDirDeg: [0, 360],
    windSpeedMs: [0, undefined],
    turbulence: [0, 1],
    visibilityKm: [0, undefined],
    cloudCover: [0, 1],
    seaState: [0, 5],
  };
  for (const key of Object.keys(o)) {
    const bound = bounds[key];
    if (bound === undefined) {
      v.fail(`${path}.${key}`, `not an environment field (expected one of ${Object.keys(bounds).join(', ')})`);
      continue;
    }
    patch[key] = v.number(o[key], `${path}.${key}`, bound[0], bound[1]);
  }
  return patch as Partial<MissionEnvironment>;
}

function validateScoring(v: Validator, raw: unknown): MissionScoring {
  const o = v.object(raw, 'scoring');
  if (o === null) return { parTimeS: 0, weights: {}, expertRequires: [] };
  const weightsRaw = v.object(o.weights, 'scoring.weights');
  const weights: Record<string, number> = {};
  if (weightsRaw !== null) {
    for (const key of Object.keys(weightsRaw)) {
      weights[key] = v.number(weightsRaw[key], `scoring.weights.${key}`, 0);
    }
  }
  return {
    parTimeS: v.number(o.parTimeS, 'scoring.parTimeS', 1),
    weights,
    expertRequires: v.stringArray(o.expertRequires, 'scoring.expertRequires'),
  };
}

/**
 * Reference integrity across the file.
 *
 * Every mistake caught here produces a mission that loads cleanly and then misbehaves silently —
 * an event that never fires, an objective that can never complete, a waypoint revealed by nothing.
 * They are the expensive class of error, which is exactly why they are checked.
 */
function crossReference(
  v: Validator,
  parts: {
    route: readonly MissionWaypoint[];
    threats: readonly MissionThreat[];
    targets: readonly MissionTarget[];
    objectives: readonly MissionObjective[];
    events: readonly MissionEvent[];
  },
): void {
  const waypointIds = new Set(parts.route.map((w) => w.id));
  const threatIds = new Set(parts.threats.map((t) => t.id));
  const targetIds = new Set(parts.targets.map((t) => t.id));
  const objectiveIds = new Set(parts.objectives.map((o) => o.id));
  const eventIds = new Set(parts.events.map((e) => e.id));

  duplicates(v, parts.route.map((w) => w.id), 'route');
  duplicates(v, parts.threats.map((t) => t.id), 'threats');
  duplicates(v, parts.targets.map((t) => t.id), 'targets');
  duplicates(v, parts.objectives.map((o) => o.id), 'objectives');
  duplicates(v, parts.events.map((e) => e.id), 'events');

  parts.route.forEach((waypoint, i) => {
    if (waypoint.revealedFrom !== undefined && !eventIds.has(waypoint.revealedFrom)) {
      v.fail(`route[${i}].revealedFrom`, `no event with id "${waypoint.revealedFrom}"`);
    }
  });

  parts.objectives.forEach((objective, i) => {
    const ref = objective.targetRef;
    if (ref === undefined) return;
    const known =
      objective.kind === 'reach'
        ? waypointIds.has(ref)
        : targetIds.has(ref) || threatIds.has(ref) || waypointIds.has(ref);
    if (!known) {
      v.fail(
        `objectives[${i}].targetRef`,
        `"${ref}" matches no ${objective.kind === 'reach' ? 'waypoint' : 'target, threat or waypoint'}`,
      );
    }
  });

  parts.events.forEach((event, i) => {
    const trigger = event.trigger;
    if (trigger.t === 'objective' && !objectiveIds.has(trigger.ref)) {
      v.fail(`events[${i}].trigger.ref`, `no objective with id "${trigger.ref}"`);
    }
    if (trigger.t === 'threatState' && !threatIds.has(trigger.ref)) {
      v.fail(`events[${i}].trigger.ref`, `no threat with id "${trigger.ref}"`);
    }
    event.actions.forEach((action, j) => {
      const at = `events[${i}].actions[${j}].ref`;
      if ((action.a === 'activateThreat' || action.a === 'deactivateThreat') && !threatIds.has(action.ref)) {
        v.fail(at, `no threat with id "${action.ref}"`);
      }
      if (action.a === 'revealWaypoint' && !waypointIds.has(action.ref)) {
        v.fail(at, `no waypoint with id "${action.ref}"`);
      }
      if (action.a === 'setObjective' && !objectiveIds.has(action.ref)) {
        v.fail(at, `no objective with id "${action.ref}"`);
      }
    });
  });
}

function duplicates(v: Validator, ids: readonly string[], path: string): void {
  const seen = new Set<string>();
  ids.forEach((id, i) => {
    if (id === '') return;
    if (seen.has(id)) v.fail(`${path}[${i}].id`, `duplicate id "${id}"`);
    seen.add(id);
  });
}
